'use client';

import Button from '@enlighten/design-system/dist/Button';
import Checkbox from '@enlighten/design-system/dist/Checkbox';
import CircularProgress from '@enlighten/design-system/dist/CircularProgress';
import FormControlLabel from '@enlighten/design-system/dist/FormControlLabel';
import Grid from '@enlighten/design-system/dist/Grid';
import TextField from '@enlighten/design-system/dist/TextField';
import Typography from '@enlighten/design-system/dist/Typography';
import Check from '@mui/icons-material/Check';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import type { Occupation } from '@/constants/news-letter';
import { occupationList } from '@/constants/news-letter';
import { useOnChangeMessage } from '@/contexts/layouts/MessageProvider';

interface NewsLetterFormData {
	email: string;
	occupation: Occupation;
	etcInput?: string;
	agree: boolean;
}

export default function Form() {
	const [loading, setLoading] = useState<boolean>(false);

	const onChangeMessage = useOnChangeMessage();

	const { control, formState, watch, setValue, handleSubmit } = useForm<NewsLetterFormData>({
		mode: 'all',
		defaultValues: {
			occupation: '예비 사업자',
		},
	});

	useEffect(
		() =>
			watch((_, { name, type }) => {
				if (type === 'change') {
					switch (name) {
						case 'occupation':
							setValue('etcInput', undefined);
							break;
						default:
							break;
					}
				}
			}).unsubscribe,
		[watch, setValue]
	);

	const onSubmit = async (formData: NewsLetterFormData) => {
		setLoading(true);
		try {
			const response = await fetch('/api/news-letter', {
				method: 'POST',
				body: JSON.stringify({
					data: {
						email: formData.email,
						occupation: formData.occupation === 'etc' ? formData.etcInput || '' : formData.occupation,
						mailingType: 'newsletter',
					},
				}),
			}).then((response) => response.json());

			if (response.status >= 200 && response.status < 300) {
				onChangeMessage({
					type: 'success',
					message: response.message,
					duration: 4000,
				});
			} else {
				throw response;
			}
		} catch (e: any) {
			onChangeMessage({
				type: 'error',
				message: e.message,
				duration: 4000,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Grid>
			<Typography
				className="required"
				fontSize={18}
				fontWeight={700}
				color="grey.A400"
				marginBottom="4px"
				sx={{
					':before': {
						content: '"* "',
						fontSize: 15,
						color: '#F25F5A',
					},
				}}
			>
				이메일 주소를 입력해 주세요
			</Typography>
			<Controller
				control={control}
				name="email"
				rules={{
					required: '이메일 주소를 입력해 주세요',
					pattern: {
						value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/i,
						message: '올바른 이메일 주소 형식으로 입력해 주세요',
					},
				}}
				render={({ field: { value, onChange }, fieldState: { error } }) => (
					<TextField
						fullWidth
						placeholder="예) success@enlighten.kr"
						value={value || ''}
						onChange={(e) => {
							onChange(e.target.value);
						}}
						error={!!error}
						helperText={error?.message || ' '}
						slotProps={{ input: { sx: { fontSize: 16 } } }}
					/>
				)}
			/>
			<Typography fontSize={18} fontWeight={700} color="grey.A400" marginY="4px">
				어떤 일을 하고 계신가요?
			</Typography>
			<Controller
				control={control}
				name="occupation"
				rules={{
					required: true,
				}}
				render={({ field: { value, onChange } }) => (
					<>
						<Grid container spacing={2}>
							{occupationList.map(({ label, value: v }) => (
								<Grid key={v} size={6}>
									<Button
										fullWidth
										className={v === value ? 'active' : undefined}
										onClick={() => {
											onChange(v);
										}}
										sx={{
											justifyContent: 'space-between',
											fontSize: 16,
											fontWeight: 400,
											color: 'grey.A200',
											bgcolor: 'grey.100',
											height: 54,
											borderRadius: '6px',
											paddingX: 3,
											':hover': {
												bgcolor: 'grey.100',
											},
											'&.active': {
												fontWeight: 500,
												color: 'primary.main',
												bgcolor: '#F5FAFF',
												borderWidth: 1,
												borderStyle: 'solid',
												borderColor: 'primary.main',
												paddingX: '23px',
											},
										}}
									>
										{label}
										{v === value && <Check fontSize="small" />}
									</Button>
								</Grid>
							))}
							{value === 'etc' && (
								<Controller
									control={control}
									name="etcInput"
									render={({ field: { value: etcInput, onChange: onChangeEtcInput } }) => (
										<TextField
											placeholder="예) 미디어, 학계"
											value={etcInput}
											onChange={(e) => {
												onChangeEtcInput(e.target.value);
											}}
											slotProps={{ input: { sx: { fontSize: 16 } } }}
										/>
									)}
								/>
							)}
						</Grid>
					</>
				)}
			/>
			<Controller
				control={control}
				name="agree"
				rules={{
					required: true,
				}}
				render={({ field: { value, onChange } }) => (
					<FormControlLabel
						control={<Checkbox size="small" />}
						value={value}
						onChange={(_, checked) => onChange(checked)}
						label={
							<Grid
								container
								alignItems="center"
								lineHeight={1.5}
								sx={{
									fontSize: 16,
									marginLeft: 1,
									'.terms': {
										textDecoration: 'underline',
										marginLeft: 1,
									},
								}}
							>
								개인정보 수집 및 이용을 확인하고 동의합니다.
								<Link className="terms" href="https://privacy.enlighten.kr/b950f03f-3434-4c34-8a67-13657d724408" target="_blank">
									약관 보기
								</Link>
							</Grid>
						}
						sx={{
							marginTop: 4,
							marginLeft: '-3px',
						}}
					/>
				)}
			/>
			<Button
				fullWidth
				disabled={!formState.isValid}
				onClick={handleSubmit(onSubmit)}
				sx={{
					fontSize: 18,
					height: 60,
					borderRadius: '6px',
					marginTop: 4,
				}}
			>
				{loading && <CircularProgress size={20} color="info" sx={{ color: 'common.white', marginRight: 2 }} />}
				구독하기
			</Button>
		</Grid>
	);
}
